import React, { useCallback, useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { IApiQueryOptions } from 'api/ApiClient';
import { Alert, Empty, EmptyTable, Input, InputIconWrapper, ModalFactory, PaginatedList, SectionHeader, StatusCheckbox } from 'components';
import { useDebounce, useQueryString } from 'hooks';
import { TipAcceptanceCode } from 'modals';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { Employees, getEmployeesRequest, selectEmployees } from 'store/Collections';
import { Employee, updateEmployeeSiteRoleRequest } from 'store/Employee';
import { selectCurrentEmployeeRole } from 'store/Employers';
import { selectSiteAcceptance } from 'store/Site';
import { IStore } from 'store/types';
import { AffiliationType, ApiQueryOperator, EmployeeType, IApiQueryParam, IEmployee } from 'types';
import { TipAcceptanceEmployee } from './components';
import { ContentHeader } from './components/styles';

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: 100%;
`;

const ALL_STATUSES = ['enabled', 'disabled'];
const getQueryName = (q?: string[]) => q?.find((query) => query.startsWith(`name${ApiQueryOperator.like}`))?.replace(`name${ApiQueryOperator.like}`, '');

const getFilters = (newStatus: string[], query?: string[]) => {
  const filters: IApiQueryParam[] = [
    {
      key: 'type',
      operator: ApiQueryOperator.in,
      value: EmployeeType.USER,
    },
  ];

  if (newStatus.length === 1) {
    filters.push({
      key: 'sites.acceptance.enabled',
      operator: newStatus[0] === 'enabled' ? ApiQueryOperator.eq : ApiQueryOperator.neq,
      value: 'true',
    });
  }

  const nameQuery = query?.find((q) => q.startsWith(`name${ApiQueryOperator.like}`));
  if (nameQuery) {
    const name = nameQuery.split(ApiQueryOperator.like);
    filters.push({
      key: name[0],
      operator: ApiQueryOperator.like,
      value: name[1],
    });
  }

  return filters;
};

const TipAcceptanceUsers = () => {
  const dispatch = useDispatch();
  const currentEmployee = useSelector(selectCurrentEmployee());
  const currentEmployeeRole = useSelector(selectCurrentEmployeeRole);
  const currentOrganizationId = currentEmployee?.organizationId || '';
  const currentSiteId = currentEmployee?.siteId || '';
  const currentSiteAcceptance = useSelector(selectSiteAcceptance());
  const { data: employees, total: totalEmployees } = useSelector(selectEmployees()) || { data: [], total: null };

  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employees.GET_EMPLOYEES_REQUEST));
  const isUpdateLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.UPDATE_EMPLOYEE_SITE_ROLE_REQUEST));
  const [urlQuery, setUrlQuery] = useQueryString('tipAcceptanceUsers', window.location, { sort: 'name', limit: 10 });
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>();
  const [statusFilters, setStatusFilters] = useState(ALL_STATUSES);

  const onQueryChange = (options: IApiQueryOptions) => {
    setUrlQuery(options);
  };

  const debouncedQuery = useDebounce(onQueryChange);

  useEffect(() => {
    if (currentOrganizationId && currentSiteAcceptance?.enabled) {
      const affiliationType = currentSiteId ? AffiliationType.SITES : AffiliationType.ORGANIZATIONS;
      const affiliationId = currentSiteId || currentOrganizationId;
      const { q } = urlQuery;
      const query = getFilters(statusFilters, q);

      const options: IApiQueryOptions = {
        ...urlQuery,
        q: query,
      };
      dispatch(getEmployeesRequest(affiliationType, affiliationId, options));
    }
  }, [dispatch, currentOrganizationId, currentSiteId, currentSiteAcceptance?.enabled, urlQuery, statusFilters]);

  const updateSiteRole = (userId: string, siteId: string, payload: any, meta?: any) => dispatch(updateEmployeeSiteRoleRequest(userId, siteId, payload, meta));

  const updateTipAcceptance = (userId: string, acceptance: boolean, onSuccess?: () => void) => {
    const meta = { onSuccess, successMessage: 'Tip acceptance updated successfully' };

    if (statusFilters.length === 1) {
      meta.onSuccess = () => {
        // refresh employees as new tip acceptance value does not match current filter
        setUrlQuery({ offset: undefined });
        onSuccess?.();
      };
    }

    updateSiteRole(userId, currentSiteId, { acceptance: { enabled: acceptance } }, meta);
  };

  const [showDisableTipModal, hideDisableTipModal] = useModal(() => {
    const employee = employees.find((emp) => emp.id === selectedEmployeeId);

    return (
      <Alert
        title={`Disable Tip Acceptance${employee ? ` for ${employee.name}` : ''}?`}
        message="Are you sure you want to disable tip acceptance for this user? They will no longer receive tips through this method. They may still be eligible to receive tips via other methods."
        onLeftAction={hideDisableTipModal}
        onRightAction={() => selectedEmployeeId && updateTipAcceptance(selectedEmployeeId, false, hideDisableTipModal)}
        leftActionLabel="Cancel"
        rightActionLabel="Disable"
        rightActionAppearance="destructive"
        isLoading={isUpdateLoading}
        isActive
        maxWidth={420}
      />
    );
  }, [employees, selectedEmployeeId, updateTipAcceptance, isUpdateLoading]);

  const [showQrCodeModal, hideQrCodeModal] = useModal(() => {
    const meta = { orgId: currentOrganizationId, toast: { message: 'Display name updated successfully' } };
    const updateDisplayName = (name: string) => updateSiteRole(selectedEmployeeId, currentSiteId, { employeeDisplayName: name }, meta);

    return (
      <ModalFactory customModal onClose={hideQrCodeModal} isActive>
        <TipAcceptanceCode
          employee={employees.find((emp) => emp.id === selectedEmployeeId)}
          siteId={currentSiteId}
          siteAcceptance={currentSiteAcceptance}
          updateDisplayName={updateDisplayName}
          isLoading={isUpdateLoading}
          onClose={hideQrCodeModal}
        />
      </ModalFactory>
    );
  }, [employees, selectedEmployeeId, currentSiteId, currentOrganizationId, isUpdateLoading]);

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value?.trim();
    debouncedQuery({ q: value ? [`name${ApiQueryOperator.like}${value}`] : undefined, offset: undefined });
  };

  const onStatusChange = useCallback(
    (newStatus: string[]) => {
      if (urlQuery.offset) {
        // clear offset to reset to first page
        setUrlQuery({ offset: undefined });
      }

      setStatusFilters(newStatus);
    },
    [urlQuery.offset]
  );

  const handleChangeAcceptance = (employee: IEmployee, acceptance: boolean) => {
    if (acceptance) {
      updateTipAcceptance(employee.id, acceptance);
    } else {
      setSelectedEmployeeId(employee.id);
      showDisableTipModal();
    }
  };

  const handleViewCode = (employee: IEmployee) => {
    setSelectedEmployeeId(employee.id);
    showQrCodeModal();
  };

  return (
    <Layout>
      <ContentHeader>
        <SectionHeader title={<StatusCheckbox label="User Status:" statuses={ALL_STATUSES} activeStatus={statusFilters} selectAll={false} onChange={onStatusChange} />} data-cy="acceptance-users-header">
          <InputIconWrapper icon="Search" iconPosition="right">
            <Input height="small" width={180} placeholder="Search by name" onChange={onSearchChange} defaultValue={getQueryName(urlQuery.q)} />
          </InputIconWrapper>
        </SectionHeader>
      </ContentHeader>

      {!isLoading && (totalEmployees === 0 || statusFilters.length === 0) ? (
        <EmptyTable>
          <Empty icon="People" message="No users found" />
        </EmptyTable>
      ) : (
        <PaginatedList total={totalEmployees} limit={urlQuery.limit} offset={urlQuery.offset} onPageChange={onQueryChange} isLoading={isLoading}>
          {!employees?.length ? (
            <TipAcceptanceEmployee isLoading />
          ) : (
            employees?.map((employee: IEmployee) => (
              <TipAcceptanceEmployee
                key={employee.id}
                employee={employee}
                currentRole={currentEmployeeRole}
                siteId={currentSiteId}
                isLoading={isLoading}
                isUpdateLoading={isUpdateLoading}
                onChangeAcceptance={(acceptance) => handleChangeAcceptance(employee, acceptance)}
                onViewCode={() => handleViewCode(employee)}
              />
            ))
          )}
        </PaginatedList>
      )}
    </Layout>
  );
};

export default TipAcceptanceUsers;
